// A remote GameView reads the wire, and the wire is not trustworthy the way core's own state is (a bad
// value here is a bug somewhere upstream — a missing field defaulting to 0 then multiplied, a stale reused
// object, a unit mix-up — never a real one core would ever produce). Refused loudly, not clamped: a clamped
// giant reveal is still a giant reveal, just a less alarming-looking wrong one. `LocalGameView` reads core
// directly and needs none of this — see gameView.ts's own header on why guards live only here.
import { LAYOUT, type Team } from '../../core/layout.js';
import { RELICS } from '../../core/relics.js';
import type { RelicLine } from '../../core/commands.js';

/** No circle/extent this game ever draws legitimately reaches past the map's own diagonal (read per call:
    the active map can change after import). */
export const maxSaneExtent = () => Math.hypot(LAYOUT.bounds.x, LAYOUT.bounds.z);
/** No zone/status/lock this game ever creates outlives two minutes — generous headroom over the longest
    real one, not a tuned value. */
export const MAX_SANE_LIFETIME = 180;
/** However many of one entity kind a match could legitimately have on screen at once, with generous
    headroom (4 heroes' worth of projectiles/pools/etc. in flight together, times a few) — a real bug
    duplicating entries looks like unbounded growth, not a large-but-plausible number. */
export const MAX_SANE_COUNT = 64;

const warned = new Set<string>();
function warnOnce(key: string, detail: string) {
  if (warned.has(key)) return;
  warned.add(key);
  console.error(`[wire] refusing ${key}: ${detail}`);
}

/** A radius, half-width or similar spatial extent. */
export function saneExtent(v: number, label: string): boolean {
  const max = maxSaneExtent();
  if (Number.isFinite(v) && v >= 0 && v <= max) return true;
  warnOnce(label, `extent ${v} is not between 0 and the map's own diagonal (~${max.toFixed(1)})`);
  return false;
}
/** A remaining-seconds or total-duration field. */
export function saneLifetime(v: number, label: string): boolean {
  if (Number.isFinite(v) && v >= 0 && v <= MAX_SANE_LIFETIME) return true;
  warnOnce(label, `lifetime ${v}s is not between 0 and ${MAX_SANE_LIFETIME}s`);
  return false;
}
/** Truncates (never grows or fabricates) an oversized array — the excess is dropped, not distributed. */
export function sanedCount<T>(arr: readonly T[], label: string): readonly T[] {
  if (arr.length <= MAX_SANE_COUNT) return arr;
  warnOnce(label, `${arr.length} entries exceeds the sane cap of ${MAX_SANE_COUNT} — truncating`);
  return arr.slice(0, MAX_SANE_COUNT);
}

/** The wire's `relics`, tolerantly: anything but a known line reads as none (a pre-protocol-14 snapshot has none). */
export function wireRelics(raw: unknown): Record<Team, RelicLine | null> {
  const o = (raw && typeof raw === 'object' ? raw : {}) as Record<string, unknown>;
  const line = (v: unknown) => (typeof v === 'string' && Object.hasOwn(RELICS, v) ? v as RelicLine : null);
  return { a: line(o.a), b: line(o.b) };
}
/** Seconds from the wire's `you` (relicCd/eyeFor): a missing or garbage value reads 0. */
export const wireSeconds = (v: unknown) => (typeof v === 'number' && Number.isFinite(v) && v > 0 ? v : 0);
/** fx `hit`'s `by` (an attacker's kind) from the wire: a short string or nothing — a pre-protocol-23 replay has none. */
export const wireHitBy = (v: unknown) => (typeof v === 'string' && v.length <= 24 ? v : undefined);
